import { Card, Input, PageHeader, Select, Spin } from 'antd';
import { useFormik } from 'formik';
import get from 'lodash/get';
import { observer } from 'mobx-react';
import moment from 'moment';
import 'moment-timezone';
import Papa from 'papaparse';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { ButtonFileInput } from '../../components/button-file-input';
import { CheckboxForm } from '../../components/checkboxes-input';
import { CmsUserHistoryBar } from '../../components/cms-user-history-bar';
import { DeepLinkLabel } from '../../components/deep-link-label';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { GameForm } from '../../components/shared-game-components/game-form';
import { Images } from '../../components/shared-game-components/game-images';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { TimezoneSelect } from '../../components/timezone-select';
import { BusinessUnitsSelectTree } from '../../components/region-selectors/business-units-select-tree';
import { useHandleFormSelectionChange } from '../../components/region-selectors/business-units-select-tree/hooks/use-handle-form-selection-change';
import { DatePicker } from '../../components/date-picker';
import { NhlGameKpi } from '../../components/nhl-game-kpi';
import { NhlGameRewardsForm } from '../../components/nhl-game-rewards';
import {
    useAuthStore,
    useNhlGamesStore,
    usePromotionStore,
} from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import { SupportedLanguages } from '../../types';
import {
    NhlGameForm,
    NhlGameKpiPeriod,
    PinData,
    UpsertNhlGamePayload,
    convertFormToNhlGamePayload,
    convertNhlGameToForm,
} from '../../types/nhl-game';
import { getFileAsString, getFileEncoding } from '../../utils/csv';
import { Text } from '../../utils/text';
import {
    DATE_TIME_FORMAT,
    getUserTimezoneOrFallback,
} from '../../utils/timezone';
import { useNavigation } from '../../utils/use-navigation';
import { useSearchParams } from '../../utils/use-search-params';
import { yupCmsHistory } from '../../utils/yup-schemas';
import style from './style.module.scss';

// Validation for the expected params
const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
});

const yupCheckboxes = yup.object({
    id: yup.number().nullable(),
    isMandatory: yup.boolean().nullable(),
    deletedAt: yup.string().nullable(),
    translations: yup.object({
        fr: yup.string(),
        en: yup.string(),
    }),
});

const YupForm = yup.object({
    stage: yup.string().required(),
    status: yup.string().required(),
    year: yup.number().required(),
    linkedGameId: yup.number().notRequired().nullable(),
    timezone: yup.string().required(),
    startDate: yup.string().required(),
    endDate: yup.string().required(),
    endGameDate: yup
        .string()
        .test(
            'shouldBeAfterEnDate',
            'EndGameDate should be after EndDate',
            function (v) {
                const EndDate = this.resolve(yup.ref('endDate'));
                return !(v == undefined || v < (EndDate as string));
            }
        ),
    title: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    description: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    image: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    detailImage: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    subTitle: yup.object({
        fr: yup.string().notRequired(),
        en: yup.string().notRequired(),
    }),
    victoryNotificationTitle: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    victoryNotificationBody: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    buttonTitle: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    checkboxes: yup.array(yupCheckboxes),
    marketingDeepLink: yup.string().nullable(),
    cmsUserEmail: yup.string().notRequired().nullable(),
    cmsUserHistory: yup.array(yupCmsHistory).nullable(),
    grandPrizeEndDate: yup.string().nullable(),
    rewards: yup.array(
        yup.object({
            id: yup.number().nullable(),
            active: yup.boolean().required(),
            promotionId: yup.number().required(),
            pucksValue: yup.number().required(),
        })
    ),
    provinces: yup
        .array(
            yup.object({
                businessUnitId: yup.number().nullable(),
                provinceId: yup.number().required(),
            })
        )
        .required()
        .min(1, 'At least one province must be selected'),
});

type Params = yup.InferType<typeof YupParams>;

const today = moment();

export type SearchParams = {
    limit?: number;
};

export const NhlGamePage = observer(() => {
    const params = useParams<Params>();
    const nhlGameStore = useNhlGamesStore();
    const promotionStore = usePromotionStore();
    const { handleProvinceSelectionChange } = useHandleFormSelectionChange();
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [isUpserting, setIsUpserting] = React.useState<boolean>(false);
    const [lastUploadDate, setLastUploadDate] = React.useState<string>();
    const [nhlGamePinsKpiPeriod, setNhlGamePinsKpiPeriod] = React.useState<
        NhlGameKpiPeriod
    >({
        startDate: '',
        endDate: '',
    });

    const search = useSearchParams<SearchParams>();

    const authStore = useAuthStore();
    const navigation = useNavigation();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }
    const formik = useFormik<NhlGameForm>({
        initialValues: {
            stage: 'regular_season',
            status: 'active',
            year: today.year(),
            linkedGameId: null,
            timezone: getUserTimezoneOrFallback(),
            startDate: null,
            grandPrizeEndDate: null,
            endDate: null,
            endGameDate: null,
            isFeatured: true,
            title: {
                fr: '',
                en: '',
            },
            description: {
                fr: '',
                en: '',
            },
            image: {
                fr: '',
                en: '',
            },
            detailImage: {
                fr: '',
                en: '',
            },
            subTitle: {
                fr: '',
                en: '',
            },
            victoryNotificationTitle: {
                fr: '',
                en: '',
            },
            victoryNotificationBody: {
                fr: '',
                en: '',
            },
            buttonTitle: {
                fr: '',
                en: '',
            },
            checkboxes: [],
            marketingDeepLink: null,
            cmsUserEmail: '',
            cmsUserHistory: [],
            rewards: [],
            provinces: [],
        },
        validateOnMount: true,
        validationSchema: YupForm,
        onSubmit: () => void 0,
    });

    const saveNhlGame = useCallback(async () => {
        formik.values.cmsUserEmail = authStore.claims?.email || null;
        setIsUpserting(true);
        try {
            const valuesWithUtcDates = convertFormToNhlGamePayload(
                formik.values
            );
            const payload = YupForm.validateSync(valuesWithUtcDates, {
                stripUnknown: true,
            });
            if (params.action === 'edit') {
                console.log(payload, 'payload');
                await nhlGameStore.updateNhlGame(
                    Number(params.id!),
                    payload as UpsertNhlGamePayload
                );
            } else {
                await nhlGameStore.createNhlGame(
                    payload as UpsertNhlGamePayload
                );
            }
        } finally {
            setIsUpserting(false);
        }
    }, [
        authStore.claims?.email,
        formik.values,
        nhlGameStore,
        params.action,
        params.id,
    ]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: saveNhlGame,
    });

    const createNhlGame = useCallback(async () => {
        await saveNhlGame();
        clearDirty(() => navigation.replaceToNhlGame());
    }, [saveNhlGame, clearDirty, navigation]);

    const updateNhlGame = useCallback(async () => {
        await saveNhlGame();
        clearDirty(() => navigation.replaceToNhlGame());
    }, [saveNhlGame, clearDirty, navigation]);

    if (!YupParams.isValidSync(params)) {
        navigation.replaceToNhlGame();
    }

    React.useEffect(() => {
        if (params.action === 'edit') {
            setIsFetching(true);
            nhlGameStore
                .getSingleGame(Number(params.id!), search.params.limit)
                .then((nhlGame) => {
                    const nhlGameForm = convertNhlGameToForm(nhlGame);
                    formik.resetForm({ values: nhlGameForm });

                    nhlGame.lastPinsUploadDate
                        ? setLastUploadDate(
                              moment(nhlGame.lastPinsUploadDate)
                                  .format('LLLL')
                                  .toString()
                          )
                        : setLastUploadDate(
                              Text.nhlGame.configContainer.lastDateMissing
                          );
                })
                .catch(() => navigation.replaceToNhlGame())
                .finally(() => setIsFetching(false));
            nhlGameStore.getNhlGamePinsKpi(Number(params.id!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        promotionStore.fetchForSelect();
    }, []);

    const checkIfHasError = (path: string) => {
        const hasError = Boolean(get(formik.errors, path));
        const isTouched = Boolean(get(formik.touched, path)); // Typescript is actually correct for the touched object, but I will use lodash.
        return hasError && isTouched;
    };

    const privatePromotions = promotionStore.promotionsForSelect.filter(
        (p) => p.isPrivate && p.type === 'coupon'
    );

    const nhlSelect = nhlGameStore.list?.results.filter(
        (nhlGame) => nhlGame.stage === 'regular_season'
    );

    const transformId = params.id ? Number(params.id) : undefined;

    const onDeleteConfirm = async () => {
        if (transformId) nhlGameStore.deleteGame(transformId);
        else NotificationManager.showError("Can't delete this game");
        navigation.goToNhlGames();
    };

    const parsePinsFile = (pins: string[][]) => {
        const parsedPins: PinData[] = [];
        pins.forEach((pin) => {
            parsedPins.push({ pin: pin[0], storeNref: pin[2] });
        });

        return parsedPins;
    };

    const onFileSelected = async (file: File) => {
        const encoding = await getFileEncoding(file);
        const text = await getFileAsString(file, encoding);

        Papa.parse<string[]>(text, {
            complete: async (results) => {
                const data = {
                    pins: parsePinsFile(results.data),
                };

                await nhlGameStore.addPinsNhlGame(data, Number(params.id));
                setLastUploadDate(moment().format('LLLL').toString());
            },
            error: () => {
                NotificationManager.showError('Error parsing the file');
            },
        });
    };

    const onPinsKpiFetch = async () => {
        if (
            nhlGamePinsKpiPeriod &&
            nhlGamePinsKpiPeriod.startDate !== '' &&
            nhlGamePinsKpiPeriod.endDate !== ''
        ) {
            // Not using our selected timezone for conversion here. Using the browser timezone as is.
            await nhlGameStore.getNhlGamePinsKpi(
                Number(params.id!),
                nhlGamePinsKpiPeriod
            );
        } else {
            await nhlGameStore.getNhlGamePinsKpi(Number(params.id!));
        }
    };

    const renderTopBarActions = () => {
        switch (params.action) {
            case 'create':
                return (
                    <SaveButton
                        onClick={createNhlGame}
                        loading={isUpserting}
                        disabled={!formik.isValid}
                    />
                );
            case 'edit':
                return (
                    <>
                        <DeleteButton
                            onClick={onDeleteConfirm}
                            loading={nhlGameStore.isDeletingGame}
                        />
                        <SaveButton
                            onClick={updateNhlGame}
                            loading={isUpserting}
                            disabled={!formik.isValid}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <PageTopBar
                title={
                    params.action === 'edit' && transformId
                        ? `${Text.navigationHeader.game} #${transformId}`
                        : Text.navigationHeader.newGame
                }
                goBack={{
                    callback: () => navigation.goToNhlGames(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            <div className={`content_with_margins ${style.cards_container}`}>
                {/* TODO: Kept for now, but will disappear soon with new designs */}
                <PageHeader
                    title={<span>Nhl-Game</span>}
                    subTitle={<span> {formik.values.title.en}</span>}
                    className={`${style.site_page_header}`}
                />

                {isFetching ? (
                    <Spin />
                ) : (
                    <>
                        <form>
                            <Card title="Config">
                                <div className={style.input_row_responsive}>
                                    <div>
                                        <span>Status</span>
                                        <br />
                                        <Select
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'status',
                                                    e
                                                )
                                            }
                                            value={formik.values.status}
                                            className={style.config_field}
                                        >
                                            <Select.Option value="active">
                                                Active
                                            </Select.Option>
                                            <Select.Option value="inactive">
                                                Inactive
                                            </Select.Option>
                                        </Select>
                                    </div>
                                    <div>
                                        <span>Year</span>
                                        <br />
                                        <Input
                                            className={style.config_field}
                                            name={`year`}
                                            type="number"
                                            value={
                                                formik.values.year ??
                                                today.year()
                                            }
                                            onChange={formik.handleChange}
                                        />
                                    </div>
                                    <div>
                                        <span>Stage</span>
                                        <br />
                                        <Select
                                            onChange={(e) =>
                                                formik.setFieldValue('stage', e)
                                            }
                                            value={formik.values.stage}
                                            className={style.config_field}
                                        >
                                            <Select.Option value="regular_season">
                                                Regular Season
                                            </Select.Option>
                                            <Select.Option value="playoff">
                                                Playoff
                                            </Select.Option>
                                        </Select>
                                    </div>
                                    {formik.values.stage === 'playoff' && (
                                        <div>
                                            <span>
                                                Link a Regular Season Game
                                            </span>
                                            <br />
                                            <Select
                                                onChange={(e) =>
                                                    formik.setFieldValue(
                                                        'linkedGameId',
                                                        e
                                                    )
                                                }
                                                value={
                                                    formik.values
                                                        .linkedGameId || ''
                                                }
                                                className={style.config_field}
                                            >
                                                {nhlSelect?.map((e) => (
                                                    <Select.Option
                                                        key={e.id}
                                                        value={e.id}
                                                        label={e.id}
                                                    >
                                                        <div>
                                                            <div>
                                                                <span>
                                                                    {e.title.en}
                                                                </span>
                                                                <span>
                                                                    {' '}
                                                                    #{e.id}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </div>
                                    )}
                                </div>
                                <div className={style.input_row_responsive}>
                                    <div>
                                        <span>Timezone</span>
                                        <br />
                                        <TimezoneSelect
                                            value={formik.values.timezone}
                                            onChange={(value) =>
                                                formik.setFieldValue(
                                                    'timezone',
                                                    value
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <span>Region</span>
                                        <br />
                                        <BusinessUnitsSelectTree
                                            value={
                                                formik.values.provinces?.map(
                                                    (p) => p.provinceId
                                                ) || []
                                            }
                                            onChange={(selectedProvinceIds) => {
                                                const updatedProvinces = handleProvinceSelectionChange(
                                                    selectedProvinceIds,
                                                    formik.values.provinces ||
                                                        []
                                                );
                                                formik.setFieldValue(
                                                    'provinces',
                                                    updatedProvinces
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className={style.input_row_responsive}>
                                    <div>
                                        <span>
                                            {
                                                Text.nhlGame.gameFields
                                                    .startDate.title
                                            }
                                        </span>
                                        <br />
                                        <DatePicker
                                            value={formik.values.startDate}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    Text.nhlGame.gameFields
                                                        .startDate.key,
                                                    e?.format(
                                                        DATE_TIME_FORMAT
                                                    ) || null
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <span>
                                            {
                                                Text.nhlGame.gameFields.endDate
                                                    .title
                                            }
                                        </span>
                                        <br />
                                        <DatePicker
                                            value={formik.values.endDate}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    Text.nhlGame.gameFields
                                                        .endDate.key,
                                                    e?.format(
                                                        DATE_TIME_FORMAT
                                                    ) || null
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <span>
                                            {
                                                Text.nhlGame.gameFields
                                                    .endGameDate.title
                                            }
                                        </span>
                                        <br />
                                        <DatePicker
                                            disabledDate={(current) => {
                                                if (formik.values.endDate)
                                                    return (
                                                        moment(
                                                            formik.values
                                                                .endDate
                                                        ).startOf('day') >
                                                        current
                                                    );
                                                else return true;
                                            }}
                                            value={formik.values.endGameDate}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    Text.nhlGame.gameFields
                                                        .endGameDate.key,
                                                    e?.format(
                                                        DATE_TIME_FORMAT
                                                    ) || null
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <span>
                                            {
                                                Text.nhlGame.gameFields
                                                    .grandPrizeEndDate.title
                                            }
                                        </span>
                                        <br />
                                        <DatePicker
                                            value={
                                                formik.values.grandPrizeEndDate
                                            }
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    Text.nhlGame.gameFields
                                                        .grandPrizeEndDate.key,
                                                    e?.format(
                                                        DATE_TIME_FORMAT
                                                    ) || null
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </Card>
                            {params.action === 'edit' && (
                                <Card
                                    title={Text.nhlGame.sectionHeaders.pins}
                                    extra={
                                        <ButtonFileInput
                                            text={
                                                Text.nhlGame.buttonText
                                                    .importCsv
                                            }
                                            onFileSelected={onFileSelected}
                                        ></ButtonFileInput>
                                    }
                                >
                                    {nhlGameStore.isUploadingPins ? (
                                        <Spin />
                                    ) : (
                                        <NhlGameKpi
                                            pinsKpi={nhlGameStore.pinsKpi}
                                            lastUploadDate={
                                                lastUploadDate ?? ''
                                            }
                                            onPinsKpiFetch={onPinsKpiFetch}
                                            kpiPeriod={nhlGamePinsKpiPeriod}
                                            setNhlGamePinsKpiPeriod={
                                                setNhlGamePinsKpiPeriod
                                            }
                                        />
                                    )}
                                </Card>
                            )}
                            <Card title="Rewards">
                                <div className={style.input_row_rewards}>
                                    <div>
                                        <NhlGameRewardsForm
                                            privatePromotions={
                                                privatePromotions
                                            }
                                            loading={
                                                promotionStore.isFetchingForSelect
                                            }
                                            value={formik.values.rewards}
                                            setValue={(v) =>
                                                formik.setFieldValue(
                                                    'rewards',
                                                    v
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </Card>
                            <Card title="Push Notification Texts">
                                <div className={style.input_row}>
                                    {SupportedLanguages.map((lang) => (
                                        <div key={lang}>
                                            <span>
                                                Victory Notification Title{' '}
                                                {lang}
                                            </span>
                                            <Input
                                                name={`victoryNotificationTitle[${lang}]`}
                                                value={
                                                    formik.values
                                                        .victoryNotificationTitle[
                                                        lang
                                                    ]
                                                }
                                                onChange={formik.handleChange}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className={style.input_row}>
                                    {SupportedLanguages.map((lang) => (
                                        <div key={lang}>
                                            <span>
                                                Victory Notification Body {lang}
                                            </span>
                                            <Input
                                                name={`victoryNotificationBody[${lang}]`}
                                                value={
                                                    formik.values
                                                        .victoryNotificationBody[
                                                        lang
                                                    ]
                                                }
                                                onChange={formik.handleChange}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Card>
                            <Card title="Images">
                                <Images
                                    image={formik.values.image}
                                    title="Feature Image"
                                    onChange={(image) => {
                                        formik.setValues({
                                            ...formik.values,
                                            image,
                                        });
                                    }}
                                    onDelete={(image) => {
                                        formik.setValues({
                                            ...formik.values,
                                            image,
                                        });
                                    }}
                                />
                                <Images
                                    image={formik.values.detailImage}
                                    title="Detail View Image"
                                    onChange={(detailImage) => {
                                        formik.setValues({
                                            ...formik.values,
                                            detailImage,
                                        });
                                    }}
                                    onDelete={(detailImage) => {
                                        formik.setValues({
                                            ...formik.values,
                                            detailImage,
                                        });
                                    }}
                                />
                            </Card>
                            <Card title="Details">
                                {/* title,subtitle,description,button */}
                                <GameForm
                                    values={formik.values}
                                    onChange={(values) => {
                                        formik.setValues({
                                            ...formik.values,
                                            ...values,
                                        });
                                    }}
                                    errors={{
                                        fr: checkIfHasError('fr'),
                                        en: checkIfHasError('en'),
                                    }}
                                />

                                <div className={style.details_inner_cards}>
                                    {formik.values.marketingDeepLink && (
                                        <DeepLinkLabel
                                            text={
                                                formik.values.marketingDeepLink
                                            }
                                        />
                                    )}

                                    <CheckboxForm
                                        values={formik.values}
                                        onChange={(values) => {
                                            formik.setValues({
                                                ...formik.values,
                                                ...values,
                                            });
                                        }}
                                        errors={{
                                            fr: checkIfHasError('fr'),
                                            en: checkIfHasError('en'),
                                        }}
                                    />
                                </div>
                            </Card>
                        </form>

                        {formik.values.cmsUserHistory &&
                            formik.values.cmsUserHistory.length > 0 && (
                                <CmsUserHistoryBar
                                    cmsUserHistory={
                                        formik.values.cmsUserHistory
                                    }
                                />
                            )}
                    </>
                )}
            </div>
        </>
    );
});
